import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Upload, X, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const blank = (barcode = "") => ({
  name: "", mrp: "", retail_price: "", barcode, category: "General", reorder_level: 5,
  image_url: "",
  stock: { shop1: 0, shop2: 0 }, expiry: { shop1: "", shop2: "" }
});

// Compress image file to data URL (JPEG, max 400x400, ~80% quality)
async function fileToCompressedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 400;
        let { width, height } = img;
        if (width > max || height > max) {
          if (width >= height) { height = Math.round((height * max) / width); width = max; }
          else { width = Math.round((width * max) / height); height = max; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AddProductDialog({ open, onOpenChange, onCreated, product, prefillBarcode }) {
  const isEdit = !!product;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(product || blank(prefillBarcode));
  const fileRef = useRef(null);

  React.useEffect(() => {
    if (open) {
      setForm(product || blank(prefillBarcode));
    }
  }, [open, product, prefillBarcode]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStock = (s, v) => setForm((f) => ({ ...f, stock: { ...f.stock, [s]: Number(v) } }));
  const setExp = (s, v) => setForm((f) => ({ ...f, expiry: { ...f.expiry, [s]: v || null } }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      set("image_url", dataUrl);
      toast.success("Image attached");
    } catch {
      toast.error("Could not read image");
    } finally {
      e.target.value = "";
    }
  };

  const submit = async () => {
    try {
      if (!form.name) return toast.error("Product name required");
      if (isEdit) {
        await api.put(`/products/${product.product_id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", form);
        toast.success("Product added");
      }
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteProd = async () => {
    if (!confirm(`Delete product "${product?.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${product.product_id}`);
      toast.success("Deleted");
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const ImageField = (
    <div className="flex gap-2 items-center">
      {form.image_url ? (
        <div className="relative">
          <img src={form.image_url} alt="" className="w-14 h-14 object-cover rounded border border-border" />
          <button type="button" onClick={() => set("image_url", "")} className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center" data-testid="prod-image-clear"><X size={10}/></button>
        </div>
      ) : (
        <div className="w-14 h-14 rounded border border-dashed border-border bg-sand flex items-center justify-center text-muted-foreground text-xs">No image</div>
      )}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} data-testid="prod-image-file" />
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => fileRef.current?.click()} data-testid="prod-image-upload-btn">
        <Upload size={14}/>Choose from device
      </Button>
      <Input className="flex-1" placeholder="or paste URL https://..." value={form.image_url?.startsWith("data:") ? "" : (form.image_url || "")} onChange={(e)=>set("image_url", e.target.value)} data-testid="prod-image-url" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Product" : "New Product"}</DialogTitle>
        </DialogHeader>
        {/* Excel-sheet style grid */}
        <div className="border border-border rounded-md overflow-hidden">
          <div className="grid grid-cols-12 bg-surface-alt label-overline text-muted-foreground border-b border-border">
            <div className="col-span-4 px-3 py-2 border-r border-border">Field</div>
            <div className="col-span-8 px-3 py-2">Value</div>
          </div>
          {[
            ["Product Name *", <Input data-testid="prod-name" disabled={isEdit && !isAdmin} value={form.name} onChange={(e)=>set("name", e.target.value)} />, true],
            ["MRP (₹) *", <Input data-testid="prod-mrp" type="number" value={form.mrp} onChange={(e)=>set("mrp", e.target.value)} />, true],
            ["Retail Price (₹) *", <Input data-testid="prod-retail" type="number" value={form.retail_price} onChange={(e)=>set("retail_price", e.target.value)} />, true],
            ["Barcode", <Input data-testid="prod-barcode" value={form.barcode} onChange={(e)=>set("barcode", e.target.value)} />, true],
            ["Category", <Input data-testid="prod-cat" value={form.category} onChange={(e)=>set("category", e.target.value)} />, true],
            ["Image", ImageField, true],
            ["Reorder Level", <Input data-testid="prod-reorder" type="number" value={form.reorder_level} onChange={(e)=>set("reorder_level", Number(e.target.value))} />, true],
            ["Open Qty - Shop 1", <Input data-testid="prod-stock-shop1" type="number" value={form.stock.shop1} onChange={(e)=>setStock("shop1", e.target.value)} />, isAdmin],
            ["Open Qty - Shop 2", <Input data-testid="prod-stock-shop2" type="number" value={form.stock.shop2} onChange={(e)=>setStock("shop2", e.target.value)} />, isAdmin],
            ["Expiry - Shop 1", <Input data-testid="prod-exp-shop1" type="date" value={form.expiry.shop1?.slice(0,10) || ""} onChange={(e)=>setExp("shop1", e.target.value)} />, true],
            ["Expiry - Shop 2", <Input data-testid="prod-exp-shop2" type="date" value={form.expiry.shop2?.slice(0,10) || ""} onChange={(e)=>setExp("shop2", e.target.value)} />, true],
          ].filter(([,, show]) => show).map(([label, comp], i) => (
            <div key={i} className="grid grid-cols-12 border-b border-border last:border-0">
              <Label className="col-span-4 px-3 py-2 border-r border-border bg-sand text-sm">{label}</Label>
              <div className="col-span-8 p-2">{comp}</div>
            </div>
          ))}
        </div>
        {!isAdmin && isEdit && (
          <p className="text-[11px] text-muted-foreground mt-2">As staff, you can edit price, barcode, category, image, and expiry. Quantity changes require an admin.</p>
        )}
        <DialogFooter>
          {isEdit && isAdmin && (
            <Button variant="outline" onClick={deleteProd} data-testid="prod-delete" className="border-destructive text-destructive hover:bg-destructive hover:text-white mr-auto gap-2">
              <Trash2 size={14}/>Delete Product
            </Button>
          )}
          <Button variant="outline" onClick={()=>onOpenChange(false)} data-testid="prod-cancel">Cancel</Button>
          <Button className="bg-terracotta hover:bg-terracotta-hover text-white" onClick={submit} data-testid="prod-save">
            {isEdit ? "Update" : "Save & Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
